// Use throughout your app instead of plain `useDispatch` and `useSelector`
import {AppDispatch, RootState} from "./store";
import {TypedUseSelectorHook, useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {OffCanvasProductModel} from "../models/domain/offCanvasProductModel";

/**
 * Use reselect to memoize dimensions pending state, to prevent unnecessary re-renders, by only returning a new object when the values change.
 */
const dimensionsPendingSelector = createSelector(
    (state: RootState) => state.addToCart.value.dimensionsPending.width,
    (state: RootState) => state.addToCart.value.dimensionsPending.height,
    (width = false, height = false) => ({width, height})
);

const useCartLoadingSelector = createSelector(
    (state: RootState) => state.cart.value.cartActionPending,
    (state: RootState) => state.cart.value.offCanvasPending,
    (cartActionPending, offCanvasPending) => cartActionPending || offCanvasPending
);

export const useAppDispatch = () => useDispatch<AppDispatch>();

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useWarrantyId = () => useAppSelector((state) => state.addToCart.value.warrantyId);
export const useDesignServiceId = () => useAppSelector((state) => state.addToCart.value.designServiceId);
export const useQuantityPending = () => useAppSelector((state) => state.addToCart.value.quantityPending);
export const useDimensions = () => useAppSelector((state) => state.addToCart.value.dimensions);
export const useDimensionsPending = () => useAppSelector(dimensionsPendingSelector);
export const useCartLoading = () => useAppSelector(useCartLoadingSelector);
export const useArtwork = () => useAppSelector((state) => state.addToCart.value.artwork);
export const useArtworkNotesPending = () => useAppSelector((state) => state.addToCart.value.artworkNotesPending);
export const useArtworkUploadPending = () => useAppSelector((state) => state.addToCart.value.artworkUploadPending);
export const useArtworkOffCanvas = () => useAppSelector((state) => state.artworkOffCanvas.value);
export const usePromotionCode = () => useAppSelector((state) => state.addToCart.value.promotionCode);
export const useMediaGallery = () => useAppSelector((state) => state.mediaGallery.value);
export const useCartStatus = () => useAppSelector((state) => state.cart.value);
export const useAddToCartOffCanvas = () => {
    const cartStateType = useAppSelector((state) => state.cart.value);
    return {
        ...cartStateType,
        product: cartStateType.product ? new OffCanvasProductModel(cartStateType.product) : undefined
    };
};